import { formatDate } from "@/lib/formatters/DateFormatter";
import { FormModelChangeHandler } from "@/types/forms/types";
import { useFormModelState } from "@/hooks/UseFormModelState";
import React, { PropsWithChildren, createContext, useState } from "react";
import {
  BookingResultData,
  CustomerResultData,
  MovementType,
  PriceCalculatorFormData,
  PriceCalculatorFormDataDefaultState,
  SearchEntityType,
} from "./types/types";

const initialFormDataState: PriceCalculatorFormData = {
  selectedCustomerNum: undefined,
  movementType: MovementType.SECTIONAL,
  sectionalDepartureTimeManual: true,
  departureTime: "11:11",
  sailingType: "P",
  moreOptionsPanelCollapsed: true,
  loadingPreferenceEnabled: false,
  loadingPreference: undefined,
  routeCode: undefined,
  multiLegRouteData: undefined,
  conditionsStandby: false,
  conditionsTransit: false,
  conditionsGoods: [],
  conditionsAncillary: [],
  vehicleType: "",
  vehicleWidth: "0",
  vehicleWeight: "0",
  vehiclePlugins: 0,
  vehicleLength: "0",
  vehicleTradeUnits: 0,
  vehicleTradeWeight: "0",
  searchEntityType: SearchEntityType.CUSTOMER,
  passengersAdults: 0,
  passengersChildren: 0,
  passengersInfants: 0,
  passengersSleepers: 0,
  cargoHazardousGoods: false,
  cargoHazardousGoodsAmountRows: 0,
  cargoHazardousGoodsTotalWeight: "0",
  sailingDate: formatDate(new Date().toISOString()),
  cargoHazardousGoodsQualified: false,
  conditionsReceiverCountryCode: undefined,
  conditionsSenderCountryCode: undefined,
  includePendingOffered: false,
  livestock: false,
  livestockType: undefined,
  routeLegs: undefined,
  vehicleShortDescription: undefined,
  bookingNumber: undefined,
  version: undefined,
  customsClassificationCode: undefined,
};

export interface PriceCalculatorContextType {
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
  setState: React.Dispatch<React.SetStateAction<PriceCalculatorFormData>>;
  defaultFormState: PriceCalculatorFormDataDefaultState;
  setDefaultFormState: React.Dispatch<
    React.SetStateAction<PriceCalculatorFormDataDefaultState>
  >;
  /** The form input as it looked when the current customer result was calculated. */
  customerCalculatedState: PriceCalculatorFormData;
  setCustomerCalculatedState: React.Dispatch<
    React.SetStateAction<PriceCalculatorFormData>
  >;
  customerResult: CustomerResultData | undefined;
  setCustomerResult: React.Dispatch<
    React.SetStateAction<CustomerResultData | undefined>
  >;
  bookingResult: BookingResultData | undefined;
  setBookingResult: React.Dispatch<
    React.SetStateAction<BookingResultData | undefined>
  >;
}

const PriceCalculatorContext = createContext<PriceCalculatorContextType | null>(
  null
);

const PriceCalculatorProvider = ({ children }: PropsWithChildren<{}>) => {
  const [defaultFormState, setDefaultFormState] =
    useState<PriceCalculatorFormDataDefaultState>({
      ...initialFormDataState,
      isInitialized: false,
    });
  const [formData, onChangeFormData, setState] =
    useFormModelState<PriceCalculatorFormData>(defaultFormState);
  const [customerCalculatedState, setCustomerCalculatedState] =
    useState<PriceCalculatorFormData>(formData);
  const [customerResult, setCustomerResult] = useState<
    CustomerResultData | undefined
  >(undefined);
  const [bookingResult, setBookingResult] = useState<
    BookingResultData | undefined
  >(undefined);

  return (
    <PriceCalculatorContext.Provider
      value={{
        formData,
        onChangeFormData,
        setState,
        setDefaultFormState,
        defaultFormState,
        customerCalculatedState,
        setCustomerCalculatedState,
        customerResult,
        setCustomerResult,
        bookingResult,
        setBookingResult,
      }}
    >
      {children}
    </PriceCalculatorContext.Provider>
  );
};

export { PriceCalculatorContext, PriceCalculatorProvider };
